import 'regenerator-runtime/runtime';
import { useState, useRef, useEffect, useCallback } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';

function TypingIndicator() {
  return (
    <div className="d-flex align-items-end gap-2 mb-3">
      <div className="bot-avatar">
        <i className="bi bi-stars"></i>
      </div>
      <div className="bubble bubble-bot typing-bubble">
        <span className="dot"></span>
        <span className="dot"></span>
        <span className="dot"></span>
      </div>
    </div>
  );
}

function BotMessage({ text, link, tab, onSpeak, isSpeaking, isVoice }) {
  return (
    <div className="message-content position-relative">
      <p className="mb-0">{text}</p>
      {link && (
        <div className="link-block">
          <a href={link} target="_blank" rel="noopener noreferrer" className="chat-link">
            <i className="bi bi-box-arrow-up-right"></i>
            <span>{tab || link}</span>
          </a>
        </div>
      )}
      {isVoice && (
        <div className="message-actions d-flex justify-content-end mt-2">
          <button
            className={`msg-action-btn ${isSpeaking ? 'speaking' : ''}`}
            onClick={onSpeak}
            title={isSpeaking ? 'Stop' : 'Listen'}
          >
            <i className={`bi ${isSpeaking ? 'bi-pause-fill' : 'bi-volume-up-fill'}`}></i>
          </button>
        </div>
      )}
    </div>
  );
}

function Message({ msg, onSpeak, isSpeaking }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`d-flex align-items-end gap-2 mb-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && (
        <div className="bot-avatar">
          <i className="bi bi-stars"></i>
        </div>
      )}
      <div className={`bubble ${isUser ? 'bubble-user' : 'bubble-bot'}`}>
        {isUser
          ? <p className="mb-0">{msg.text}</p>
          : <BotMessage
              text={msg.text}
              link={msg.link}
              tab={msg.tab}
              onSpeak={onSpeak}
              isSpeaking={isSpeaking}
              isVoice={msg.isVoice}
            />
        }
      </div>
    </div>
  );
}

export default function App() {
  const [messages, setMessages] = useState([
    { role: 'bot', text: "Hi there! I'm your AI assistant. Ask me anything to get started." },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [currentlySpeaking, setCurrentlySpeaking] = useState(null);
  const [continuousMode, setContinuousMode] = useState(false);
  const bottomRef = useRef(null);

  // Refs to track continuous mode and typing inside async callbacks
  const continuousModeRef = useRef(false);
  const typingRef = useRef(false);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  // Keep refs in sync with state
  useEffect(() => {
    continuousModeRef.current = continuousMode;
  }, [continuousMode]);

  useEffect(() => {
    typingRef.current = typing;
  }, [typing]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  // Update input text with transcript in real-time
  useEffect(() => {
    if (transcript) {
      setInput(transcript);
    }
  }, [transcript]);

  // ── Restart microphone after TTS finishes (continuous mode) ──────────────
  const restartMicAfterSpeech = useCallback(() => {
    if (!continuousModeRef.current) return;
    if (!browserSupportsSpeechRecognition || !isMicrophoneAvailable) return;
    // Small delay so the browser doesn't immediately pick up TTS audio
    setTimeout(() => {
      if (!continuousModeRef.current) return; // might have been stopped by user
      resetTranscript();
      SpeechRecognition.startListening({ continuous: false });
    }, 600);
  }, [browserSupportsSpeechRecognition, isMicrophoneAvailable, resetTranscript]);

  // ── Text-to-Speech ────────────────────────────────────────────────────────
  const toggleSpeak = useCallback((text, index, onEndCallback) => {
    if (!('speechSynthesis' in window)) return;

    if (currentlySpeaking === index) {
      window.speechSynthesis.cancel();
      setCurrentlySpeaking(null);
    } else {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.onend = () => {
        setCurrentlySpeaking(null);
        if (onEndCallback) onEndCallback();
      };
      utterance.onerror = () => {
        setCurrentlySpeaking(null);
        if (onEndCallback) onEndCallback();
      };
      setCurrentlySpeaking(index);
      window.speechSynthesis.speak(utterance);
    }
  }, [currentlySpeaking]);

  // ── Send message ─────────────────────────────────────────────────────────
  const send = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || typingRef.current) return;

    const isVoice = localStorage.getItem('isVoicePrompt') === 'true';

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setCurrentlySpeaking(null);
    }

    setInput('');
    resetTranscript();
    // Stop listening while awaiting reply (avoids picking up bot's TTS)
    SpeechRecognition.stopListening();

    const userMsgIndex = messages.length;
    setMessages(prev => [...prev, { role: 'user', text: trimmed, isVoice }]);
    setTyping(true);

    try {
      const response = await fetch(process.env.REACT_APP_CHAT_BOT_BACKEND_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: {
            question: trimmed,
            is_voice: isVoice
          },
          config: {},
          kwargs: {}
        })
      });

      const data = await response.json();
      let reply = data?.output?.answer?.Answer || data?.output || "I didn't quite understand the response format.";
      let link = data?.output?.answer?.link || null;
      let tab = data?.output?.answer?.Tab || null;

      const botMsgIndex = userMsgIndex + 1;
      setMessages(prev => [...prev, { role: 'bot', text: reply, link, tab, isVoice }]);
      localStorage.setItem('isVoicePrompt', 'false');

      if (isVoice) {
        // After TTS ends, restart mic if still in continuous mode
        toggleSpeak(reply, botMsgIndex, restartMicAfterSpeech);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMsg = 'Sorry, I encountered an error connecting to the backend.';
      const botMsgIndex = messages.length + 1;
      setMessages(prev => [...prev, { role: 'bot', text: errorMsg, isVoice }]);
      localStorage.setItem('isVoicePrompt', 'false');
      if (isVoice) {
        toggleSpeak(errorMsg, botMsgIndex, restartMicAfterSpeech);
      }
    } finally {
      setTyping(false);
    }
  }, [input, resetTranscript, messages.length, toggleSpeak, restartMicAfterSpeech]);

  // ── When speech recognition finishes a result, auto-send in continuous mode ──
  useEffect(() => {
    if (!listening && continuousModeRef.current && transcript.trim()) {
      // Mic just stopped with a captured transcript → send it
      localStorage.setItem('isVoicePrompt', 'true');
      send(transcript);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening]);

  // ── Toggle continuous voice mode ─────────────────────────────────────────
  const toggleContinuousMode = () => {
    if (!browserSupportsSpeechRecognition) {
      alert('Speech recognition is not supported in this browser. Please try Chrome or Edge.');
      return;
    }
    if (!isMicrophoneAvailable) {
      alert('Microphone is not available or permission denied.');
      return;
    }

    if (continuousMode) {
      // ── STOP continuous mode ──
      setContinuousMode(false);
      continuousModeRef.current = false;
      SpeechRecognition.stopListening();
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      setCurrentlySpeaking(null);
      resetTranscript();
      setInput('');
    } else {
      // ── START continuous mode ──
      setContinuousMode(true);
      continuousModeRef.current = true;
      localStorage.setItem('isVoicePrompt', 'true');
      resetTranscript();
      // Use continuous:false so each utterance fires onend, triggering auto-send
      SpeechRecognition.startListening({ continuous: false });
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      SpeechRecognition.stopListening();
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    };
  }, []);

  return (
    <div className="chat-page d-flex align-items-center justify-content-center min-vh-100">
      <div className="chat-card d-flex flex-column">

        <div className="chat-header d-flex align-items-center justify-content-between px-3 py-2">
          <div className="d-flex align-items-center gap-2">
            <div className="bot-avatar bot-avatar-lg">
              <i className="bi bi-stars"></i>
            </div>
            <div>
              <div className="header-name">AI Assistant</div>
              <div className="d-flex align-items-center gap-1">
                <span className="status-dot"></span>
                <span className="header-status">Online</span>
              </div>
            </div>
          </div>
          {continuousMode && (
            <div className="continuous-badge">
              <i className="bi bi-mic-fill me-1"></i>
              Hands-Free Active
            </div>
          )}
        </div>

        <div className="chat-messages flex-grow-1 overflow-auto p-3">
          <div className="date-label text-center mb-3">Today</div>
          {messages.map((msg, i) => (
            <Message
              key={i}
              msg={msg}
              onSpeak={() => toggleSpeak(msg.text, i)}
              isSpeaking={currentlySpeaking === i}
            />
          ))}
          {typing && <TypingIndicator />}
          <div ref={bottomRef} />
        </div>

        <div className="chat-input-row d-flex align-items-center gap-2 px-3 py-2">
          <button
            className={`voice-btn ${continuousMode ? 'continuous-active' : listening ? 'listening' : ''}`}
            onClick={toggleContinuousMode}
            title={continuousMode ? 'Stop hands-free mode' : 'Start hands-free voice mode'}
          >
            {continuousMode ? (
              <i className="bi bi-stop-fill"></i>
            ) : (
              <i className={`bi ${listening ? 'bi-mic-fill' : 'bi-mic'}`}></i>
            )}
            {(continuousMode || listening) && <span className="pulse-ring"></span>}
          </button>
          <input
            className="form-control chat-input"
            value={input}
            onChange={e => {
              setInput(e.target.value);
              if (!continuousMode) localStorage.setItem('isVoicePrompt', 'false');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                localStorage.setItem('isVoicePrompt', 'false');
                send();
              }
            }}
            placeholder={
              continuousMode
                ? listening
                  ? '🎙️ Listening...'
                  : typing
                  ? '⏳ Waiting for reply...'
                  : '🔄 Hands-Free Mode — speak anytime'
                : listening
                ? 'Listening...'
                : 'Type a message...'
            }
          />
          <button className="send-btn" onClick={() => {
            localStorage.setItem('isVoicePrompt', 'false');
            send();
          }}>
            <i className="bi bi-send-fill"></i>
          </button>
        </div>

      </div>
    </div>
  );
}